/**
 * The picture slot: `<figure data-kb-shot="key" data-kb-caption="what it shows">`.
 *
 * An atom, and empty on purpose. The slot is a HOLE in the article, not a
 * picture: the file that fills it lives beside the guide in `KbArticle.shots`,
 * keyed by `data-kb-shot`, so re-seeding the text never loses an uploaded
 * screenshot. That is why the figure carries no children and why the editor must
 * never let anybody type inside one - a caret in there would write text into a
 * node the reader draws as a dashed box.
 *
 * Both attributes are load-bearing. `parseShotSlots()` on the server refuses a
 * slot whose key is not lower-case-hyphenated, refuses a duplicate key, and
 * refuses an empty caption, so all three are also what the editor has to keep.
 */
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { ShotNodeView } from "../ShotNodeView";

export const KbShot = Node.create({
  name: "kbShot",
  group: "block",
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      key: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-kb-shot") ?? "",
        renderHTML: (attrs) => ({ "data-kb-shot": String(attrs.key ?? "") }),
      },
      caption: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-kb-caption") ?? "",
        renderHTML: (attrs) => ({ "data-kb-caption": String(attrs.caption ?? "") }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "figure[data-kb-shot]" }];
  },

  renderHTML({ HTMLAttributes }) {
    // No content hole: a slot is empty markup, and giving it one would let the
    // serialiser write `<figure ...> </figure>` with a stray text node inside.
    return ["figure", mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ShotNodeView);
  },
});
